import { useEffect, useState } from 'react';
import { createRoute, stripSearchParams } from '@tanstack/react-router';

import { rootRoute, useAppRouterInitial } from '../routerBase';
import {
  CAPACITIES_SEARCH_DEFAULTS,
  parseCapacitiesSearch,
} from '../../hooks/useCapacitiesRouterSearch';
import { CapacitiesPageSkeleton } from '../../components/Skeleton/CapacitiesPageSkeleton';
import { EditSkeleton } from '../../components/Skeleton/EditSkeleton';
import { useTranslation } from '../../hooks/useTranslation';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { CAPABILITIES } from '../../lib/capabilities';
import { requireCapability } from '../requireCapability';
// Type-only imports so the per-route bundles stay split — the runtime modules
// are loaded via dynamic import() inside each route's effect (see below).
import type CapacityManagement from '../../components/CapacityManagement/index';
import type CapacityBulkEdit from '../../components/CapacityBulkEdit/index';

export const capacitiesIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/capacities',
  validateSearch: parseCapacitiesSearch,
  search: { middlewares: [stripSearchParams(CAPACITIES_SEARCH_DEFAULTS)] },
  component: function CapacitiesIndexRoute() {
    const { servingDate, initialCapacities, currentUser } = useAppRouterInitial();
    const { t } = useTranslation();
    useDocumentTitle(t('common.page_titles.capacities'));
    const [Component, setComponent] = useState<typeof CapacityManagement | null>(null);
    useEffect(() => {
      import('../../components/CapacityManagement/index').then((mod) => {
        setComponent(() => mod.default);
      });
    }, []);
    if (!Component) {
      // Use the same i18n key the Phlex SSR skeleton renders with so the
      // hydrated aria-label byte-matches the server HTML.
      return <CapacitiesPageSkeleton label={t('capacities.loading')} />;
    }
    const fallbackServingDate = new Date().toLocaleDateString('en-CA', {
      timeZone: 'Asia/Singapore',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    return (
      <Component
        servingDate={servingDate ?? fallbackServingDate}
        initialData={initialCapacities}
        currentUser={currentUser}
      />
    );
  },
});

export const capacitiesBulkEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/capacities/bulk_edit',
  validateSearch: parseCapacitiesSearch,
  search: { middlewares: [stripSearchParams(CAPACITIES_SEARCH_DEFAULTS)] },
  // RBAC: bulk edit is a write-only surface. The guard redirects read-only
  // users (no capacities.write) to the single-date page before this route's
  // component runs, so they never reach the editable bulk form even via a
  // direct URL. The server policy enforces this too; this is UX.
  beforeLoad: requireCapability(CAPABILITIES.CAPACITIES_WRITE, {
    to: '/capacities',
    search: { serving_date: '' },
  }),
  component: function CapacitiesBulkEditRoute() {
    const { t } = useTranslation();
    useDocumentTitle(t('common.page_titles.capacities_bulk_edit'));
    const [Component, setComponent] = useState<typeof CapacityBulkEdit | null>(null);
    useEffect(() => {
      import('../../components/CapacityBulkEdit/index').then((mod) => {
        setComponent(() => mod.default);
      });
    }, []);
    if (!Component) {
      return <EditSkeleton label={t('common.loading')} />;
    }
    return <Component />;
  },
});
